import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Star, Users, MessageCircle, ChevronDown, ShieldCheck, Globe, ScrollText, Sparkles, GraduationCap, BadgeCheck, CheckCircle2 } from "lucide-react";
import BirthForm from "../components/BirthForm";
import PaywallModal from "../components/PaywallModal";
import { SmartOfferBanner, HindiPromoBanner } from "../components/PromoBanners";
import SEO, { orgSchema, websiteSchema } from "../components/SEO";
import { useI18n } from "../contexts/I18nContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { PRIVACY_POLICY_URL } from "../lib/legalLinks";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Animated star field component
const STARS = Array.from({ length: 80 }, (_, i) => ({
  id: i, x: Math.random() * 100, y: Math.random() * 100,
  size: Math.random() * 2.5 + 0.5, delay: Math.random() * 4, duration: Math.random() * 3 + 2
}));
function StarField() {
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      {STARS.map(s => (
        <div key={s.id} style={{
          position: "absolute", left: `clamp(0px, calc(${s.x}% - 4px), calc(100% - 4px))`, top: `${s.y}%`,
          width: `${s.size}px`, height: `${s.size}px`, borderRadius: "50%", background: "white",
          animation: `twinkle ${s.duration}s ${s.delay}s ease-in-out infinite`
        }} />
      ))}
      {/* Nebula blobs */}
      <div style={{ position: "absolute", width: "min(700px, 100%)", height: "500px", background: "radial-gradient(circle, rgba(91,30,140,0.25) 0%, transparent 70%)", top: "-150px", right: 0, borderRadius: "50%" }} />
      <div style={{ position: "absolute", width: "min(600px, 100%)", height: "600px", background: "radial-gradient(circle, rgba(45,11,69,0.4) 0%, transparent 70%)", bottom: "-100px", left: 0, borderRadius: "50%" }} />
    </div>
  );
}

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const ZODIAC_PROMPTS = ["When will I get married?", "Career growth in next 2 years?", "Financial stability?", "Foreign settlement chances?", "Relationship compatibility?"];

export default function HomePage() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const explainerRef = useRef(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const { t, lang } = useI18n();
  const { price, priceOld } = useCurrency();
  const [testimonials, setTestimonials] = useState([]);
  const [accurateCount, setAccurateCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/accuracy-count`)
      .then(r => setAccurateCount(r.data?.total || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/testimonials?lang=${lang}`)
      .then(r => { if (alive) setTestimonials(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setTestimonials([]); });
    return () => { alive = false; };
  }, [lang]);


  return (
    <div className="home-root" style={{ minHeight: "100vh", background: "#120021", position: "relative" }}>
      <SEO
        title="AstroFate — Vedic AI Astrology, Kundli & Live Astrologer Consultations"
        description="AstroFate helps you understand yourself, navigate challenges, and grow with confidence through personalized insights, AI guidance, and ancient wisdom. Generate your free Kundli, ask an AI astrologer, or book a live consultation."
        schema={{ "@context": "https://schema.org", "@graph": [orgSchema, websiteSchema] }}
      />
      <StarField />

      {/* Hero Section */}
      <section style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 16px 40px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
          <HindiPromoBanner />
          <div className="hero-grid">
            
            {/* Left: Hero text */}
            <div className="animate-fadeInUp">
              {/* One credibility line: who built it + the method. Strongest trust
                  signal, so it sits above the headline on every screen size. */}
              <div data-testid="hero-founder-pill" className="hero-badge founder-pill" style={{
                display: "inline-flex", alignItems: "center", gap: "9px", marginBottom: "20px",
                background: "linear-gradient(120deg, rgba(255,209,102,0.16), rgba(157,78,221,0.2))",
                border: "1px solid rgba(255,209,102,0.5)", borderRadius: "50px", padding: "8px 16px",
                boxShadow: "0 0 24px rgba(255,209,102,0.14)",
              }}>
                <GraduationCap size={16} style={{ color: "#FFD166", flexShrink: 0 }} />
                <span style={{ color: "#fff", fontSize: "0.82rem", fontWeight: 700, lineHeight: 1.45 }}>
                  {lang === "hi"
                    ? <>IIT खड़गपुर ग्रेजुएट <span style={{ color: "#FFD166" }}>· ~5 वर्ष ज्योतिष · आधुनिक AI सटीकता</span></>
                    : <>IIT Kharagpur graduate <span style={{ color: "#FFD166" }}>· ~5 yrs of Jyotish · Modern AI precision</span></>}
                </span>
              </div>
              <h1 className="font-cinzel hero-title" style={{ lineHeight: 1.15, fontWeight: "700", marginBottom: "16px" }}>
                <span style={{ color: "white" }}>{lang === "hi" ? "अपने करियर, रिश्ते या ज़िंदगी के अगले फैसले के लिए " : "Get practical Vedic clarity for your next "}</span>
                <span className="gradient-text-gold" style={{ textShadow: "0 0 40px rgba(255,209,102,0.3)" }}>{lang === "hi" ? "व्यावहारिक वैदिक स्पष्टता।" : "career, relationship, or life decision."}</span>
              </h1>
              <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "1.05rem", lineHeight: 1.7, marginBottom: "28px", maxWidth: "540px" }}>
                {lang === "hi"
                  ? "एक सवाल। आपकी कुंडली से जुड़ा एक स्पष्ट जवाब।"
                  : "One question. One clear answer, straight from your chart."}
              </p>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button data-testid="hero-free-chat-btn" onClick={() => navigate("/chat")} className="btn-gold" style={{ fontSize: "0.98rem" }}>
                  {lang === "hi" ? "एक मुफ़्त कुंडली सवाल पूछें" : "Ask one FREE chart question"}
                </button>
                <button data-testid="hero-kundli-report-btn" onClick={() => navigate("/kundli")} className="btn-ghost" style={{ fontSize: "0.98rem" }}>
                  {lang === "hi" ? "अपनी कुंडली रिपोर्ट समझें" : "Understand your Kundli report"}
                </button>
              </div>
              <p style={{ color: "rgba(255,209,102,0.9)", fontSize: "0.82rem", marginTop: "14px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", lineHeight: 1.5 }}>
                <span style={{ background: "linear-gradient(135deg,#F4B942,#FFD166)", color: "#120021", borderRadius: "5px", padding: "2px 8px", fontSize: "0.72rem", fontWeight: 900, letterSpacing: "0.5px" }}>
                  FREE
                </span>
                {lang === "hi"
                  ? "पहला सवाल — कोई card नहीं · कुंडली रिपोर्ट भी मुफ़्त"
                  : "first question — no card needed · Kundli report is free too"}
              </p>
              {accurateCount > 0 && (
                <div data-testid="hero-accuracy-proof" style={{
                  display: "inline-flex", alignItems: "center", gap: "9px", marginTop: "16px",
                  background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.35)",
                  borderRadius: "50px", padding: "8px 15px",
                }}>
                  <CheckCircle2 size={14} style={{ color: "#86efac", flexShrink: 0 }} />
                  <span style={{ color: "#86efac", fontSize: "0.8rem", fontWeight: 700 }}>
                    {accurateCount.toLocaleString()}
                  </span>
                  <span style={{ color: "rgba(255,255,255,0.68)", fontSize: "0.8rem" }}>
                    {lang === "hi" ? ' पठन क्लाइंट्स ने "सटीक" बताए' : ` reading${accurateCount === 1 ? "" : "s"} marked accurate by clients`}
                  </span>
                </div>
              )}
              <div className="hero-chips" style={{ display: "flex", gap: "10px", marginTop: "32px", flexWrap: "wrap" }}>
                {(lang === "hi"
                  ? ["प्रामाणिक वैदिक ज्योतिष", "English & हिन्दी", "निजी और सुरक्षित", "4.6★ Google पर"]
                  : ["Authentic Vedic Jyotish", "English & हिन्दी", "Private & secure", "4.6★ on Google"]
                ).map(chip => (
                  <span key={chip} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,209,102,0.22)", borderRadius: "50px", padding: "7px 14px", color: "rgba(255,255,255,0.75)", fontSize: "0.78rem", fontWeight: 600 }}>
                    <span style={{ color: "#FFD166" }}>✦</span> {chip}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Birth Form */}
            <div ref={formRef} className="animate-fadeInUp delay-200">
              <BirthForm />
            </div>
          </div>
        </div>
      </section>

      {/* Who's behind AstroFate — credibility before the explainer */}
      <section data-testid="founder-section" style={{ padding: "56px 16px 8px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div className="glass-card founder-card" style={{
            padding: "34px", border: "1px solid rgba(157,78,221,0.28)",
            background: "linear-gradient(120deg, rgba(157,78,221,0.12), rgba(255,209,102,0.05) 65%, transparent)",
            display: "grid", gridTemplateColumns: "180px 1fr", gap: "32px", alignItems: "center",
          }}>
            {/* Monogram crest */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="founder-crest" style={{
                width: "160px", height: "160px", borderRadius: "50%", flexShrink: 0,
                border: "1px solid rgba(255,209,102,0.45)",
                background: "radial-gradient(circle at 30% 25%, rgba(157,78,221,0.45), rgba(18,0,33,0.9))",
                boxShadow: "0 0 50px rgba(157,78,221,0.28), inset 0 0 30px rgba(0,0,0,0.5)",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px",
              }}>
                <GraduationCap size={26} style={{ color: "#FFD166" }} />
                <div className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.32rem", letterSpacing: "1px", lineHeight: 1 }}>IIT</div>
                <div className="font-cinzel" style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.78rem", letterSpacing: "2.5px" }}>KGP</div>
                <div style={{ width: "42px", height: "1px", background: "rgba(255,209,102,0.4)", margin: "3px 0" }} />
                <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.64rem", letterSpacing: "1px", textTransform: "uppercase" }}>
                  {lang === "hi" ? "संस्थापक" : "Founder"}
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-cinzel" style={{ fontSize: "clamp(1.45rem, 2.6vw, 2.1rem)", color: "#FFD166", lineHeight: 1.25, marginBottom: "14px" }}>
                {lang === "hi"
                  ? "इंजीनियरिंग की सटीकता, 5 वर्षों की ज्योतिष साधना"
                  : "Engineering precision, 5 years of Jyotish practice"}
              </h2>
              <p style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.98rem", lineHeight: 1.75, marginBottom: "20px", maxWidth: "620px" }}>
                {lang === "hi"
                  ? "AstroFate को एक IIT खड़गपुर ग्रेजुएट ने बनाया है, जिन्होंने पिछले पाँच वर्ष शास्त्रीय वैदिक ज्योतिष के अध्ययन और अभ्यास में लगाए हैं। हर पठन आपकी असली जन्म कुंडली से गणना होता है — लाहिरी अयनांश और सटीक ग्रह गणित के साथ — और फिर उसे एक पारंपरिक ज्योतिषी की तरह पढ़ा जाता है। यानी हर जवाब आपकी व्यक्तिगत कुंडली पर आधारित होता है।"
                  : "AstroFate is built by an IIT Kharagpur graduate who has spent the last five years studying and practising classical Vedic astrology. Every reading is computed from your real birth chart — Lahiri ayanamsa, precise ephemeris maths — and then interpreted the way a traditional Jyotishi would. Every answer comes from your own personalised Kundli."}
              </p>
              <div style={{ display: "flex", gap: "9px", flexWrap: "wrap", marginBottom: "22px" }}>
                {(lang === "hi"
                  ? ["IIT खड़गपुर एलुमनस", "5 वर्ष कुंडली पठन", "शास्त्रीय पराशरी पद्धति", "व्यक्तिगत कुंडली आधारित उत्तर"]
                  : ["IIT Kharagpur alumnus", "~5 years reading charts", "Classical Parashari method", "Answers from your personalised Kundli"]
                ).concat(accurateCount > 0
                  ? [lang === "hi" ? `${accurateCount.toLocaleString()} पठन "सटीक" चिह्नित` : `${accurateCount.toLocaleString()} readings marked accurate`]
                  : []
                ).map(item => (
                  <span key={item} style={{ display: "inline-flex", alignItems: "center", gap: "7px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,209,102,0.22)", borderRadius: "50px", padding: "7px 14px", color: "rgba(255,255,255,0.78)", fontSize: "0.78rem", fontWeight: 600 }}>
                    <BadgeCheck size={13} style={{ color: "#FFD166" }} /> {item}
                  </span>
                ))}
              </div>
              <button data-testid="founder-cta-btn" onClick={() => navigate("/chat")} className="btn-gold" style={{ fontSize: "0.92rem" }}>
                {lang === "hi" ? "मेरी कुंडली पढ़वाएं" : "Have my chart read"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works — 3-step explainer */}
      <section ref={explainerRef} style={{ padding: "72px 16px 32px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "44px" }}>
            <div className="cosmic-divider" />
            <h2 className="font-cinzel" style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", color: "#FFD166" }}>{lang === "hi" ? "यह कैसे काम करता है" : "How it works"}</h2>
            <p style={{ color: "rgba(255,255,255,0.5)", marginTop: "10px", fontSize: "0.95rem" }}>{lang === "hi" ? "तीन आसान कदम — दो मिनट से भी कम में।" : "Three simple steps — in under two minutes."}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px" }}>
            {(lang === "hi"
              ? [
                  { title: "जन्म विवरण जोड़ें", desc: "तारीख, समय और स्थान — इनसे आपकी सटीक जन्म कुंडली बनती है।" },
                  { title: "एक असली सवाल पूछें", desc: "करियर, रिश्ता या कोई ज़िंदगी का फैसला — जो सच में मायने रखता है।" },
                  { title: "कुंडली-आधारित मार्गदर्शन पाएं", desc: "स्पष्ट पठन और व्यावहारिक अगले कदम — न कि गोलमोल बातें।" },
                ]
              : [
                  { title: "Add your birth details", desc: "Date, time & place — these build your accurate birth chart." },
                  { title: "Ask one real question", desc: "About career, a relationship, or a life decision that actually matters." },
                  { title: "Get chart-linked guidance", desc: "A clear reading plus practical next steps — not vague generalities." },
                ]
            ).map((s, i) => (
              <div key={i} data-testid={`how-step-${i}`} className="glass-card glass-card-hover" style={{ padding: "30px", position: "relative" }}>
                <div className="font-cinzel" style={{ width: "44px", height: "44px", borderRadius: "50%", border: "1.5px solid rgba(255,209,102,0.5)", color: "#FFD166", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem", fontWeight: 700, marginBottom: "18px" }}>{i + 1}</div>
                <h3 className="font-cinzel" style={{ fontSize: "1.12rem", color: "white", marginBottom: "10px" }}>{s.title}</h3>
                <p style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.7, fontSize: "0.9rem" }}>{s.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "36px" }}>
            <button data-testid="explainer-cta-btn" onClick={() => navigate("/chat")} className="btn-gold">{lang === "hi" ? "अपना मुफ़्त सवाल पूछें" : "Ask your free question"}</button>
          </div>
        </div>
      </section>

      {/* Trust strip — specific product facts (no generic claims) */}
      <section style={{ padding: "8px 16px 40px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div className="glass-card" style={{ padding: "28px 24px", border: "1px solid rgba(255,209,102,0.16)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "22px" }}>
              {[
                { icon: <Star size={18} fill="#FFD166" style={{ color: "#FFD166" }} />, label: lang === "hi" ? "4.6★ Google पर" : "4.6★ on Google", sub: lang === "hi" ? "प्रमाणित Google रिव्यू" : "Verified Google reviews" },
                { icon: <ScrollText size={18} style={{ color: "#FFD166" }} />, label: lang === "hi" ? "प्रामाणिक ज्योतिष" : "Authentic Jyotish", sub: lang === "hi" ? "लाहिरी अयनांश + सटीक ग्रह गणना" : "Lahiri ayanamsa + precise ephemeris maths" },
                { icon: <ShieldCheck size={18} style={{ color: "#FFD166" }} />, label: lang === "hi" ? "निजी और सुरक्षित" : "Private & secure", sub: lang === "hi" ? "आपके जन्म विवरण कभी बेचे नहीं जाते" : "Your birth details are never sold" },
                { icon: <Globe size={18} style={{ color: "#FFD166" }} />, label: lang === "hi" ? "English और हिन्दी" : "English & Hindi", sub: lang === "hi" ? "दोनों भाषाओं में उपलब्ध" : "Available in both languages" },
              ].map((it, i) => (
                <div key={i} data-testid={`trust-item-${i}`} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <div style={{ flexShrink: 0, marginTop: "2px" }}>{it.icon}</div>
                  <div>
                    <div style={{ color: "white", fontWeight: 700, fontSize: "0.92rem" }}>{it.label}</div>
                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", lineHeight: 1.5 }}>{it.sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <p data-testid="trust-disclaimer" style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", lineHeight: 1.6, marginTop: "22px", paddingTop: "18px", borderTop: "1px solid rgba(255,255,255,0.08)", textAlign: "center" }}>
              {lang === "hi"
                ? "ज्योतिष मार्गदर्शन और आत्म-चिंतन के लिए है — भविष्य की घटनाओं की गारंटी नहीं। यह चिकित्सा, कानूनी या वित्तीय सलाह का विकल्प नहीं है।"
                : "Astrology is for guidance and self-reflection — not a guarantee of future events. It is not a substitute for professional medical, legal, or financial advice."}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: "80px 16px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <div className="cosmic-divider" />
            <h2 className="font-cinzel" style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", color: "#FFD166" }}>{t("home.featuresHeading")}</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
            {[
              { icon: "✦", title: t("home.feat1Title"), desc: t("home.feat1Desc"), cta: t("home.feat1Cta"), path: "/kundli" },
              { icon: "◈", title: t("home.feat2Title"), desc: t("home.feat2Desc"), cta: t("home.feat2Cta"), path: "/chat" },
              { icon: "◉", title: t("home.feat3Title"), desc: t("home.feat3Desc"), cta: t("home.feat3Cta"), path: "/consultation" }
            ].map((f, i) => (
              <div key={f.title} data-testid={`feature-card-${i}`} className="glass-card glass-card-hover" style={{ padding: "32px" }}>
                <div style={{ fontSize: "2rem", color: "#FFD166", marginBottom: "16px", fontFamily: "monospace" }}>{f.icon}</div>
                <h3 className="font-cinzel" style={{ fontSize: "1.15rem", color: "white", marginBottom: "12px" }}>{f.title}</h3>
                <p style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.7, marginBottom: "20px", fontSize: "0.9rem" }}>{f.desc}</p>
                <button onClick={() => navigate(f.path)} className="btn-ghost" style={{ padding: "8px 20px", fontSize: "0.85rem" }}>{f.cta} →</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Question CTA */}
      <section style={{ padding: "80px 16px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "center" }}>
          <div className="glass-card" style={{ padding: "48px", border: "1px solid rgba(255,209,102,0.2)", background: "linear-gradient(135deg, rgba(91,30,140,0.2), rgba(45,11,69,0.4))" }}>
            <div style={{ fontSize: "3rem", marginBottom: "16px" }}>◎</div>
            <h2 className="font-cinzel" style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", color: "#FFD166", marginBottom: "12px" }}>{t("home.freeQTitle")}</h2>
            <p style={{ color: "rgba(255,255,255,0.6)", marginBottom: "28px", lineHeight: 1.7 }}>
              {t("home.freeQDesc")}
            </p>
            <div className="zodiac-chips" style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center", marginBottom: "28px" }}>
              {ZODIAC_PROMPTS.map(p => (
                <span key={p} onClick={() => navigate("/chat")} style={{ background: "rgba(157,78,221,0.15)", border: "1px solid rgba(157,78,221,0.3)", borderRadius: "50px", padding: "6px 14px", fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={e => e.target.style.borderColor = "rgba(157,78,221,0.7)"}
                  onMouseLeave={e => e.target.style.borderColor = "rgba(157,78,221,0.3)"}>
                  {p}
                </span>
              ))}
            </div>
            <button data-testid="free-question-cta-btn" onClick={() => navigate("/chat")} className="btn-gold">{t("home.freeQCta")}</button>
          </div>
        </div>
      </section>

      {/* Stage-aware offer: free chat → ₹51 AI plan → ₹249 consult */}
      <section style={{ padding: "0 16px 40px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <SmartOfferBanner />
        </div>
      </section>

      {/* Pricing Section */}
      <section style={{ padding: "80px 16px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div className="cosmic-divider" />
            <h2 className="font-cinzel" style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", color: "#FFD166" }}>{t("home.pricingHeading")}</h2>
          </div>
          <div className="hero-grid" style={{ gap: "24px", maxWidth: "900px", margin: "0 auto" }}>
            {[
              { plan: t("home.priceAiChat"), price: price("ai_chat"), period: t("home.priceAiChatPeriod"), color: "#FFD166", badge: t("home.priceAiChatBadge"), features: [t("home.priceAiChatFeat1"), t("home.priceAiChatFeat2"), t("home.priceAiChatFeat3"), t("home.priceAiChatFeat4")], testid: "pricing-ai-chat" },
              { plan: t("home.priceConsult"), price: price("consultation"), oldPrice: priceOld("consultation"), period: t("home.priceConsultPeriod"), color: "#9D4EDD", badge: t("home.priceConsultBadge"), features: [t("home.priceConsultFeat1"), t("home.priceConsultFeat2"), t("home.priceConsultFeat3"), t("home.priceConsultFeat4")], testid: "pricing-consultation" }
            ].map(item => (
              <div key={item.plan} data-testid={item.testid} className="glass-card" style={{ padding: "32px", border: `1px solid ${item.color}25`, textAlign: "center" }}>
                <span className="premium-badge" style={{ background: item.oldPrice ? "linear-gradient(135deg, #ef4444, #f97316)" : `linear-gradient(135deg, ${item.color}, ${item.color}bb)` }}>{item.badge}</span>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "10px", margin: "16px 0 4px" }}>
                  <div className="font-cinzel" style={{ fontSize: "2.5rem", color: item.color, fontWeight: "700" }}>{item.price}</div>
                  {item.oldPrice && (
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "1.05rem", textDecoration: "line-through" }}>{item.oldPrice}</div>
                  )}
                </div>
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", marginBottom: "16px" }}>{item.period}</div>
                <div style={{ fontWeight: "700", color: "white", fontSize: "1.1rem", marginBottom: "20px" }}>{item.plan}</div>
                <ul style={{ listStyle: "none", textAlign: "left", marginBottom: "24px" }}>
                  {item.features.map(f => <li key={f} style={{ display: "flex", alignItems: "center", gap: "8px", color: "rgba(255,255,255,0.65)", fontSize: "0.88rem", marginBottom: "10px" }}>
                    <span style={{ color: item.color }}>✓</span> {f}
                  </li>)}
                </ul>
                <button data-testid={`pricing-btn-${item.testid}`} onClick={() => setShowPaywall(true)} className={item.color === "#FFD166" ? "btn-gold" : "btn-purple"} style={{ width: "100%" }}>{t("home.priceGetStarted")}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials — admin-managed; section hides itself until one is approved */}
      {testimonials.length > 0 && (
        <section style={{ padding: "80px 16px", position: "relative", zIndex: 1 }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <div className="cosmic-divider" />
              <h2 className="font-cinzel" style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", color: "#FFD166" }}>{t("home.testimonialsHeading")}</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
              {testimonials.map((item, i) => (
                <div key={item.id} data-testid={`testimonial-${i}`} className="glass-card" style={{ padding: "28px" }}>
                  <div style={{ color: "#FFD166", marginBottom: "12px" }}>{"\u2605".repeat(item.rating || 5)}</div>
                  <p style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.7, fontStyle: "italic", marginBottom: "16px", fontSize: "0.92rem" }}>&ldquo;{item.text}&rdquo;</p>
                  <div>
                    <div style={{ color: "white", fontWeight: "600", fontSize: "0.9rem" }}>{item.name}</div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>
                      {[item.location, item.product].filter(Boolean).join(" \u00b7 ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "48px 16px 32px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "16px" }}>
            <img
              src="/logo-mark.png"
              alt="AstroFate"
              style={{ width: "34px", height: "34px", objectFit: "contain" }}
            />
            <span className="font-cinzel" style={{ fontSize: "1.2rem", color: "#FFD166" }}>AstroFate</span>
          </div>
          <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.82rem", marginBottom: "20px" }}>Ancient wisdom. Modern AI. Your destiny awaits.</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "24px", flexWrap: "wrap", marginBottom: "16px" }}>
            {[
              ["Privacy Policy", PRIVACY_POLICY_URL],
              ["Terms & Conditions", "/terms"],
              ["Refund Policy", "/refunds"],
              ["Delete Account", "/account/delete"],
              ["Contact Us", "/contact"],
              ["Blog", "/blog"],
            ].map(([label, path]) => (
              <button
                key={label}
                data-testid={`footer-link-${path.replace(/\//g, "-").replace(/^-/, "")}`}
                onClick={() => { navigate(path); window.scrollTo(0, 0); }}
                style={{ background: "none", border: "none", padding: 0, color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", cursor: "pointer", transition: "color 0.2s ease" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#FFD166"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
              >
                {label}
              </button>
            ))}
          </div>
          <p style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.75rem" }}>© 2025 AstroFate. All rights reserved.</p>
        </div>
      </footer>

      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} onSuccess={() => navigate("/chat")} />}
    </div>
  );
}
